import type { TaskStatus, TaskSummary } from '@fos/shared/contracts/work';
import { todayLocal } from '@fos/shared/time';
import { useCallback, useEffect, useState } from 'react';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { toast } from '../../../ui/Toast';

/** Słowniki formularza i filtrów (klienci, projekty, usługi, osoby) — w zakresie zalogowanej osoby. */
export function useTaskOptions(enabled = true) {
  return trpc.tasks.options.useQuery(undefined, { staleTime: 60_000, enabled });
}

/** Dzisiejsza data (Europe/Warsaw), odświeżana co minutę (przejście przez północ). */
export function useToday(): string {
  const [today, setToday] = useState(() => todayLocal());
  useEffect(() => {
    const id = window.setInterval(() => setToday(todayLocal()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return today;
}

/** Media query (np. tablica tylko na desktopie). */
export function useMediaQuery(query: string): boolean {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : true);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
}

export const DESKTOP_QUERY = '(min-width: 768px)';

/** Komunikat błędu zapisu zadania (konflikt wersji = dane się zmieniły, odświeżamy). */
export function taskErrorToast(err: unknown): void {
  if (appErrorCode(err) === 'CONFLICT') {
    toast.error('Zadanie zmieniło się w międzyczasie', { description: 'Odświeżyliśmy widok. Spróbuj jeszcze raz.' });
    return;
  }
  toast.error(errorMessage(err, 'Nie udało się zapisać zmian.'));
}

/**
 * Zapisy zadań z odświeżeniem wszystkich widoków (lista, tablica, karta, Dziś): po każdej zmianie
 * `tasks.*` jest unieważniane, więc zmiana statusu z tablicy od razu widać na liście i odwrotnie.
 */
export function useTaskMutations() {
  const utils = trpc.useUtils();
  const refresh = () => utils.tasks.invalidate();
  const onError = (err: unknown) => {
    taskErrorToast(err);
    void refresh();
  };
  const update = trpc.tasks.update.useMutation({ onSuccess: refresh, onError });
  const archive = trpc.tasks.archive.useMutation({ onSuccess: refresh, onError });
  const restore = trpc.tasks.restore.useMutation({ onSuccess: refresh, onError });
  const quickDone = useQuickDone();

  /** Kółko przy zadaniu: „Gotowe” od razu z „Cofnij” (useQuickDone); przy gotowym — z powrotem „Do zrobienia”. */
  const toggleDone = (task: QuickDoneTask) => {
    if (task.status !== 'done') {
      quickDone(task);
      return;
    }
    setStatusInCaches(utils, task.id, 'todo');
    update.mutate({ id: task.id, version: task.version, status: 'todo' });
  };

  return { update, archive, restore, toggleDone, quickDone, refresh };
}

export type QuickDoneTask = Pick<TaskSummary, 'id' | 'version' | 'status' | 'title'>;

/**
 * „Przyjmij” przy zgłoszeniu od klienta na liście i w profilu klienta / projektu (przegląd portalu 17.10.2026; jak
 * „Przyjmij” na tablicy): zgłoszenie od razu w „Do zrobienia”, toast z „Cofnij” (powrót do „Zgłoszeń”). Klient zobaczy
 * w portalu „Zaplanowane”; przyjęcie nie wysyła maila.
 */
export function useAcceptRequest() {
  const utils = trpc.useUtils();
  return useCallback(
    (task: QuickDoneTask) => {
      if (task.status !== 'request') return;
      setStatusInCaches(utils, task.id, 'todo');
      const saving = utils.client.tasks.update.mutate({ id: task.id, version: task.version, status: 'todo' });
      saving.then(
        () => void utils.tasks.invalidate(),
        (err: unknown) => {
          setStatusInCaches(utils, task.id, 'request');
          taskErrorToast(err);
          void utils.tasks.invalidate();
        },
      );
      toast.success('Zgłoszenie przyjęte', {
        description: `${task.title} — w „Do zrobienia”`,
        duration: QUICK_DONE_TOAST_MS,
        action: {
          label: 'Cofnij',
          onClick: () => {
            setStatusInCaches(utils, task.id, 'request');
            saving
              .then(
                (saved) =>
                  utils.client.tasks.update
                    .mutate({ id: saved.id, version: saved.version, status: 'request' })
                    .catch(taskErrorToast),
                () => undefined,
              )
              .finally(() => void utils.tasks.invalidate());
          },
        },
      });
    },
    [utils],
  );
}
type Utils = ReturnType<typeof trpc.useUtils>;

/**
 * Status zadania od razu w pamięci podręcznej wszystkich list (`tasks.list`: lista, profil klienta / projektu)
 * i karty (`tasks.get`); z tablicy (`tasks.board`) zadanie znika przy „Gotowe”. Serwer potwierdza w tle,
 * a odświeżenie po zapisie (`tasks.invalidate`) układa listy według filtrów.
 */
export function setStatusInCaches(utils: Utils, taskId: string, status: TaskStatus): void {
  void utils.tasks.list.cancel();
  utils.tasks.list.setQueriesData(undefined, {}, (old) =>
    old ? { ...old, items: old.items.map((t) => (t.id === taskId ? { ...t, status } : t)) } : old,
  );
  utils.tasks.get.setData({ id: taskId }, (old) => (old ? { ...old, status } : old));
  if (status === 'done') {
    void utils.tasks.board.cancel();
    utils.tasks.board.setQueriesData(undefined, {}, (old) => {
      if (!old) return old;
      const had = old.columns.some((c) => c.items.some((t) => t.id === taskId));
      if (!had) return old;
      return {
        ...old,
        doneThisWeek: old.doneThisWeek + 1,
        columns: old.columns.map((c) =>
          c.items.some((t) => t.id === taskId)
            ? { ...c, items: c.items.filter((t) => t.id !== taskId), total: Math.max(0, c.total - 1) }
            : c,
        ),
      };
    });
  }
}

/** Czas toastu „Oznaczono jako gotowe” z „Cofnij” (ms). */
export const QUICK_DONE_TOAST_MS = 6000;

/**
 * Szybkie „Gotowe” (DECISIONS 17.10.2026 „Zadania łatwiejsze w użyciu”): okrągły znacznik na liście, tablicy
 * i w profilu klienta / projektu. Optymistycznie — zadanie od razu wygląda na gotowe (i znika z tablicy), toast
 * „Cofnij” pojawia się od razu; zapis istniejącą procedurą `tasks.update` (status) w tle. „Cofnij” przywraca
 * POPRZEDNI status (np. „W toku”, nie zawsze „Do zrobienia”) — po potwierdzeniu zapisu, z jego nową wersją.
 * Wywołania przez `utils.client`, więc zapis i „Cofnij” działają także po zniknięciu wiersza z ekranu.
 */
export function useQuickDone() {
  const utils = trpc.useUtils();
  return useCallback(
    (task: QuickDoneTask) => {
      const previous = task.status;
      // zgłoszenie od klienta najpierw się przyjmuje (useAcceptRequest); „Cofnij” z „Gotowe” do „Zgłoszenia” i tak
      // by nie przeszło (reguła statusu), a mail „zakończone” do klienta już by wyszedł — przegląd portalu 17.10.2026
      if (previous === 'done' || previous === 'request') return;
      setStatusInCaches(utils, task.id, 'done');
      const saving = utils.client.tasks.update.mutate({ id: task.id, version: task.version, status: 'done' });
      saving.then(
        () => void utils.tasks.invalidate(),
        (err: unknown) => {
          setStatusInCaches(utils, task.id, previous);
          taskErrorToast(err);
          void utils.tasks.invalidate();
        },
      );
      toast.success('Oznaczono jako gotowe', {
        description: task.title,
        duration: QUICK_DONE_TOAST_MS,
        action: {
          label: 'Cofnij',
          onClick: () => {
            setStatusInCaches(utils, task.id, previous);
            saving
              .then(
                (saved) =>
                  utils.client.tasks.update
                    .mutate({ id: saved.id, version: saved.version, status: previous })
                    .catch(taskErrorToast),
                // zapis „Gotowe” się nie udał — status już wrócił, komunikat był
                () => undefined,
              )
              .finally(() => void utils.tasks.invalidate());
          },
        },
      });
    },
    [utils],
  );
}
